import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PageHelpButton } from '@/src/renderer/components/common/PageHelpButton'
import { useTranslation } from 'react-i18next'
import type { LucideIcon } from 'lucide-react'
import { useScrollbarVisibility } from '@/src/renderer/hooks/useScrollbarVisibility'
import { CheckCircleFilledIcon } from '../../../../components/icons/CheckCircleFilledIcon'
import { AreaIcon } from '../../../../components/icons/AreaIcon'
import { showSuccessToast } from '../../../../components/common/SuccessToast'
import { useTabActions } from '../../../../contexts/TabManagerContext'
import { useDataSource } from '../../../../lib/dataSource'
import type { Task } from '../../../../lib/db/models'
import { useIsActiveTab } from '../../../../pages/TabStack/ActiveTabContext'
import { EmptyState } from '../../components/EmptyState'
import { TaskList } from '../../components/TaskList'
import { SelectionHullScope } from '../../components/SelectionHull'
import { LazyRowScope } from '../../components/LazyRows'
import type { TaskEntryData } from '../../components/TaskEntry'
import { useTaskOperations } from '../../hooks/useTaskOperations'
import { usePendingPageJump } from '../../hooks/usePendingPageJump'
import { useOptimisticTaskOverrides } from '../../hooks/useOptimisticTaskOverrides'
import { useCompletionGrace } from '../../hooks/useCompletionGrace'
import { useMidnightRefresh } from '../../hooks/useMidnightRefresh'
import { useScrollFades } from '../../hooks/useScrollFades'
import { useProjectNavigation, type NavSegment } from '../../hooks/useProjectNavigation'
import { useAppTaskMenu, menuAnchorOf } from '../../hooks/useAppTaskMenu'
import { anchorTaskId, useTaskMenuShortcuts } from '../../hooks/useTaskMenuShortcuts'
import { taskToEntryData } from '../../utils/taskToEntryData'
import { setPendingTaskJump } from '@/src/renderer/lib/taskJump/pendingTaskJump'
import { DoneStats } from './components/DoneStats'
import { DoneDayGroup } from './components/DoneDayGroup'
import { useDoneTasks } from './hooks/useDoneTasks'
import { countThisWeek, countToday, dayLabel, groupByDay, streakDays, type DoneDay } from './utils/doneBuckets'
import '../TaskViews.scss'
import './DonePage.scss'
import { CalmIllustration } from '../../components/CalmIllustration'
import { useStableRandomIndex } from '../../hooks/useStableRandomIndex'
import { usePageBreadcrumb } from '../../hooks/usePageBreadcrumb'
import { ListFocusContext } from '../../utils/listFocus'

/** So viele Varianten stehen unter `…emptyTitles` in den Sprachdateien. */
const EMPTY_TITLE_COUNT = 9

interface DonePageProps {
  lifeSpace: 'personal' | 'work' | 'home'
}

/** Wie viele Tage die Seite anfangs zeigt und je Nachschub dazunimmt. */
const DAYS_PER_PAGE = 30

/** „16:42" — dieselbe Schreibweise wie am **Geschafft-Schalter**. */
function formatTime(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

type DoneTaskEntry = {
  id: string
  at: Date
  entry: TaskEntryData
  projectId: string | null
  task: Task
  /** Gebuchte **Fokuszeit** in Sekunden — steht auch neben `entry`, weil der Tageskopf sie summiert. */
  focusedSeconds: number | null
}

/**
 * **Geschafft** (CONTEXT.md): alles Erledigte eines Lebensbereichs, nach Tag
 * gruppiert, mit Uhrzeit — und drei Zahlen darüber.
 *
 * Die Zeilen sind gewöhnliche Aufgabenzeilen mit allem, was sie können; nur
 * das Menü ist ein anderes (`menuKind="done"`). Der Haken holt zurück,
 * doppelt gesichert: Die **Liegezeit** läuft rückwärts, und eine Meldung mit
 * „Rückgängig" sagt, wohin die Aufgabe gegangen ist.
 */
export const DonePage: React.FC<DonePageProps> = ({ lifeSpace }) => {
  const { t } = useTranslation()
  // Neun Titel für den leeren Zustand, einer pro Besuch (24.09.2026).
  const emptyTitleIndex = useStableRandomIndex(EMPTY_TITLE_COUNT)
  const isActive = useIsActiveTab()
  const rawTasks = useDoneTasks({ lifeSpace, isActive })
  const dataSource = useDataSource()
  const { openTab } = useTabActions()
  const scrollRef = useRef<HTMLDivElement>(null)
  // Die Spalte um den Scroller trägt die Verläufe — nicht der Scroller selbst
  // (dort klemmte sie sein Inhaltskasten), dieselbe Bauart wie im **Projekt**.
  const listColumnRef = useRef<HTMLDivElement>(null)
  const { visible: scrollbarVisible, onScroll: handleScrollbar } =
    useScrollbarVisibility({ ref: scrollRef })

  // Die Verläufe oben und unten — derselbe Baustein wie Projekt und Kurz notiert.
  useScrollFades(scrollRef, listColumnRef)

  // Mitternacht verschiebt „Heute" — die Rechnung hängt an diesem Schlüssel.
  const todayKey = useMidnightRefresh()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const now = useMemo(() => new Date(), [todayKey])

  const { optimisticTaskOverrides, setOptimisticTaskOverride } = useOptimisticTaskOverrides(rawTasks)

  // Die **Liegezeit**, rückwärts: Wer hier den Haken herausnimmt, sieht die
  // Zeile zwei Sekunden stehen. `graceIds` hält die Kennungen; der Filter
  // unten lässt sie durch, obwohl `completedAt` schon weg ist.
  const { graceIds, markCompleted: markReopened } = useCompletionGrace()

  // Wer den Haken herausnimmt, fällt aus der Abfrage: Sie läuft über den Index
  // auf `completedAt`, und der kennt die Aufgabe ohne Zeitpunkt nicht mehr.
  // Die Liegezeit braucht die Zeile aber noch — also bleibt die zuletzt
  // gesehene Fassung gemerkt und springt ein, solange die Kennung in
  // `graceIds` liegt.
  const lastSeenRef = useRef(new Map<string, Task>())
  const effectiveTasks = useMemo(() => {
    if (!rawTasks) return rawTasks
    const present = new Set<string>()
    const kept = rawTasks.flatMap(task => {
      present.add(task.id)
      lastSeenRef.current.set(task.id, task)
      const override = optimisticTaskOverrides.get(task.id)
      const merged = override ? { ...task, ...override } : task
      if (merged.deletedAt) return []
      if (merged.completedAt == null && !graceIds.has(merged.id)) return []
      return [merged]
    })
    for (const id of graceIds) {
      if (present.has(id)) continue
      const seen = lastSeenRef.current.get(id)
      if (seen && !seen.deletedAt) kept.push({ ...seen, completedAt: null, status: 'todo' })
    }
    return kept
  }, [rawTasks, optimisticTaskOverrides, graceIds])

  const { handleUpdateTask, handleToggleComplete, handleDeleteTask } = useTaskOperations({
    t, setOptimisticTaskOverride, tasks: effectiveTasks,
  })

  // Projektnamen für die Marke an der Zeile und für die Meldung.
  const projects = dataSource.useLifeSpaceProjects(lifeSpace)
  const projectNameMap = useMemo(() => {
    const map = new Map<string, string>()
    projects?.forEach(p => map.set(p.id, p.name))
    return map
  }, [projects])
  const projectIconMap = useMemo(() => {
    const map = new Map<string, { icon: string | null; iconColor: string | null }>()
    projects?.forEach(p => { if (p.icon) map.set(p.id, { icon: p.icon, iconColor: p.iconColor ?? null }) })
    return map
  }, [projects])

  /**
   * Der Haken heraus. Die Zeile bleibt (Liegezeit), die Aufgabe geht zurück,
   * und die Meldung nennt das Ziel — mit einem Weg zurück, der die ALTE
   * Uhrzeit wiederherstellt statt eine neue zu setzen.
   */
  const handleToggle = useCallback((ids: Set<string>) => {
    const tasks = (effectiveTasks ?? []).filter(task => ids.has(task.id))
    const reopened = tasks.filter(task => task.completedAt != null)
    markReopened(ids)
    handleToggleComplete(ids)
    if (reopened.length === 0) return

    const snapshot = reopened.map(task => ({ id: task.id, completedAt: task.completedAt as Date, cloudId: task.cloudId }))
    const first = reopened[0]
    const projectName = first.projectId ? projectNameMap.get(first.projectId) : undefined
    const message = reopened.length > 1
      ? t('done.toast.reopenedMany', { count: reopened.length })
      : projectName
        ? t('done.toast.reopenedInProject', { project: projectName })
        : t('done.toast.reopened')
    showSuccessToast({
      message,
      action: {
        label: t('common.undo'),
        onClick: async () => {
          for (const row of snapshot) {
            setOptimisticTaskOverride(row.id, { completedAt: row.completedAt, status: 'completed' })
            await dataSource.updateTask(row.id, { completedAt: row.completedAt, status: 'completed' })
          }
        },
      },
    })
  }, [effectiveTasks, markReopened, handleToggleComplete, projectNameMap, t, setOptimisticTaskOverride, dataSource])

  /**
   * **Hin zur Aufgabe** — derselbe Weg wie das Aufwecken aus **Ruhend**: den Sprung
   * merken, den Projekt-Tab öffnen. Die Projektseite löst ihn ein, wählt die Aufgabe aus
   * und holt sie ins Bild; steht dort der **Canvas** maximiert, fährt er vorher auf
   * geteilt zurück (`revealTaskInList`).
   *
   * Eine Aufgabe **ohne Projekt** hat kein Ziel — dann bleibt man hier stehen.
   */
  const jumpToProject = useCallback((taskId: string, options: { reopened?: boolean } = {}) => {
    const task = effectiveTasks?.find(entry => entry.id === taskId)
    if (!task?.projectId) return
    const projectName = projectNameMap.get(task.projectId) ?? ''
    setPendingTaskJump(task.projectId, taskId, options)
    openTab({
      id: `${lifeSpace}-project-${task.projectId}-${Date.now()}`,
      path: `/home/${lifeSpace}/project/${task.projectId}`,
      title: projectName,
      icon: AreaIcon as unknown as LucideIcon,
      lifeSpace,
      isDirty: false,
      isPinned: false,
    })
  }, [effectiveTasks, projectNameMap, openTab, lifeSpace])

  /** „Im Projekt zeigen" — nur hin, ohne die Aufgabe anzufassen. */
  const handleShowInProject = useCallback((taskId: string) => jumpToProject(taskId), [jumpToProject])

  /**
   * „Wieder öffnen" — Haken heraus UND hin zur Aufgabe (entschieden 2026-09-12).
   *
   * Nicht derselbe Weg wie der **Haken** an der Zeile, obwohl beide dasselbe Feld
   * ändern: Wer abhakt, bleibt auf **Geschafft** und liest die Meldung; wer die
   * MENÜZEILE wählt, sagt „und bring mich hin". Deshalb hängt sie an `onReopen`
   * und nicht an `onToggleComplete`.
   *
   * Die Meldung mit „Rückgängig" bleibt: Sie trägt den Rückweg, nicht nur die Auskunft.
   */
  const handleReopen = useCallback((taskId: string) => {
    handleToggle(new Set([taskId]))
    // `reopened`: Die Projektseite darf sie nicht mehr für etwas hinter dem
    // **Geschafft-Schalter** halten — der Schreibvorgang nach Dexie läuft noch.
    jumpToProject(taskId, { reopened: true })
  }, [handleToggle, jumpToProject])

  // ── Die Rechnung ──
  //
  // Eine Zeile in der **Liegezeit** hat ihr `completedAt` schon verloren, soll
  // aber noch an ihrem Platz stehen. Der Platz ist der alte Zeitpunkt — also
  // wird er gemerkt, solange er da ist, und in der Liegezeit nachgeschlagen.
  const lastCompletedAtRef = useRef(new Map<string, Date>())

  /*
    Die **Fokuszeit für die Tagessummen** (3e). Die ZEILEN bekommen ihre Zahl
    von `TaskList`, die sie sich je Liste selbst holt; hier geht es allein um
    die Summe im Tageskopf, und die lässt sich aus den Zeilen nicht
    zurücklesen, ohne über eine Darstellung zu gehen.

    Die Kennungsliste muss gemerkt sein, sonst entstünde je Render ein frisches
    Array. Nur **gebuchte** Abschnitte — eine Aufgabe auf **Geschafft** ist
    ohnehin fertig.
  */
  const focusIds = useMemo(
    () => (effectiveTasks ?? []).map(task => task.id),
    [effectiveTasks],
  )
  const focusSecondsByTask = dataSource.useFocusSeconds(focusIds)

  const entries = useMemo<DoneTaskEntry[]>(
    () => (effectiveTasks ?? []).flatMap(task => {
      if (task.completedAt) lastCompletedAtRef.current.set(task.id, task.completedAt)
      const at = task.completedAt ?? lastCompletedAtRef.current.get(task.id)
      if (!at) return []
      const entry = taskToEntryData(task)
      const projectName = task.projectId ? projectNameMap.get(task.projectId) : undefined
      const focusedSeconds = focusSecondsByTask?.get(task.id) ?? null
      return [{
        id: task.id,
        at,
        projectId: task.projectId,
        task,
        focusedSeconds,
        // Das Extra wird ÜBER den gemerkten Entwurf gelegt, nicht in ihn
        // geschrieben: `taskToEntryData` merkt sich je `Task` in einer WeakMap
        // und kennt nur Felder, die am `Task` selbst hängen.
        // `focusedSeconds` steht bewusst NICHT in `entry`: `TaskList` holt die
        // Zeit für jede Liste selbst (3e). Hier wird sie nur für die
        // **Tagessumme** im Kopf gebraucht.
        entry: {
          ...entry,
          projectName: projectName ?? entry.projectName,
          projectIcon: task.projectId ? projectIconMap.get(task.projectId)?.icon ?? null : null,
          projectIconColor: task.projectId ? projectIconMap.get(task.projectId)?.iconColor ?? null : null,
          completedTime: formatTime(at),
        },
      }]
    }),
    [effectiveTasks, projectNameMap, projectIconMap, focusSecondsByTask],
  )

  const days = useMemo(() => groupByDay(entries, now), [entries, now])
  const stats = useMemo(() => ({
    today: countToday(entries, now),
    week: countThisWeek(entries, now),
    streak: streakDays(new Set(days.map(day => day.key)), now),
  }), [entries, days, now])

  // ── Nachladen beim Scrollen: die Seite zeigt erst 30 Tage, dann mehr ──
  const [visibleDayCount, setVisibleDayCount] = useState(DAYS_PER_PAGE)
  useEffect(() => { setVisibleDayCount(DAYS_PER_PAGE) }, [lifeSpace])
  const visibleDays = useMemo(() => days.slice(0, visibleDayCount), [days, visibleDayCount])
  const hasMore = days.length > visibleDayCount

  /*
    **Eine Tastatur über alle Tage.** Jeder Tag ist seine eigene `TaskList`,
    und für sich genommen endete die Pfeiltaste an seiner letzten Zeile. Also
    derselbe Griff wie im **Projekt**: `useProjectNavigation` führt EINE
    Auswahl und EINEN Fokus über alle Tage, die Listen werden passiv
    (`externalSelection`). Ein Tag ist dabei ein Segment ohne Sektion.

    Nur die GEZEIGTEN Tage stehen in der Liste — was noch nicht nachgeladen
    ist, kann man auch nicht anfahren.
  */
  const navSegments = useMemo<NavSegment[]>(
    () => visibleDays.map(day => ({ section: null, tasks: day.entries.map(e => e.task) })),
    [visibleDays],
  )
  const navigation = useProjectNavigation({
    segments: navSegments,
    // Über der ersten Zeile liegt nichts, was den Fokus nehmen könnte.
    onEscapeTop: () => {},
    onDelete: handleDeleteTask,
    onToggleComplete: handleToggle,
    isActive,
  })

  // Klick auf eine erledigte Aufgabe ohne Projekt in „Favoriten“, die nicht in Kurz notiert steht (#210).
  // Steht sie weiter unten als geladen, bleibt es beim Öffnen der Seite.
  usePendingPageJump({
    page: 'done', lifeSpace, isActive, ready: rawTasks !== undefined, rootRef: scrollRef,
    select: taskId => { navigation.selectTask(taskId) },
  })

  /*
    **Das Menü „Aufgaben“ in der Menüleiste** (#257): hier, was auch das Rechtsklick-Menü einer
    erledigten Aufgabe kann — Wieder öffnen, Im Projekt zeigen, Löschen.
  */
  const menuAnchor = useMemo(() => {
    const focused = navigation.focusedNavIndex !== null ? navigation.flatNavList[navigation.focusedNavIndex] : null
    const id = anchorTaskId(
      focused?.type === 'task' ? focused.id : null,
      navigation.flatNavList.filter(item => item.type === 'task').map(item => item.id),
      navigation.selectedIds,
    )
    return menuAnchorOf(id ? effectiveTasks?.find(task => task.id === id) : null)
  }, [navigation.focusedNavIndex, navigation.flatNavList, navigation.selectedIds, effectiveTasks])
  // ⌘J (#257): Im Projekt zeigen — das einzige Kürzel des Aufgaben-Menüs, das hier etwas tut.
  useTaskMenuShortcuts({
    enabled: isActive,
    getAnchorTaskId: () => menuAnchor?.id ?? null,
    onShowInProjectShortcut: handleShowInProject,
  })
  useAppTaskMenu({
    enabled: isActive,
    anchor: menuAnchor,
    multiSelected: navigation.selectedIds.size > 1,
    onComplete: navigation.completeSelection,
    onShowInProject: handleShowInProject,
    onDelete: navigation.deleteSelection,
  })

  // Beim Aktivwerden des Tabs den Wrapper fokussieren, sonst muss man nach
  // jedem Tab-Wechsel erst klicken, bevor die Pfeiltasten greifen (TabStack
  // legt inaktive Slots auf `display: none`, der Fokus fällt auf `<body>`).
  useEffect(() => {
    if (!isActive) return
    const rafId = requestAnimationFrame(() => {
      const active = document.activeElement
      const typingTag = active?.tagName
      if (typingTag === 'INPUT' || typingTag === 'TEXTAREA') return
      navigation.wrapperRef.current?.focus({ preventScroll: true })
    })
    return () => cancelAnimationFrame(rafId)
  }, [isActive, navigation.wrapperRef])
  const sentinelRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!hasMore || !isActive) return
    const sentinel = sentinelRef.current
    const root = scrollRef.current
    if (!sentinel || !root || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver((records) => {
      if (records.some(r => r.isIntersecting)) setVisibleDayCount(n => n + DAYS_PER_PAGE)
    }, { root, rootMargin: '200px 0px' })
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMore, isActive, visibleDays.length])

  // Erst mit Daten ist „leer“ eine Aussage — vor der Lade-Rückkehr, Hooks stehen nicht bedingt.
  const isEmpty = rawTasks !== undefined && entries.length === 0
  // Der Seitenname in der Breadcrumb: leer fest, sonst sobald die Überschrift weggerollt ist.
  const titleRef = useRef<HTMLHeadingElement>(null)
  usePageBreadcrumb({
    title: t('done.title'), isActive, loaded: rawTasks !== undefined, isEmpty, titleRef, scrollerRef: scrollRef,
  })

  if (rawTasks === undefined) {
    return <div className="task-view done-page" aria-busy="true" />
  }


  return (
    <div className="task-view done-page">
      <div ref={listColumnRef} className="done-page__list-column">
      <div
        className={`done-page__scroll${scrollbarVisible ? ' done-page__scroll--scroll-visible' : ''}${isEmpty ? ' done-page__scroll--empty' : ''}`}
        ref={scrollRef}
        onScroll={handleScrollbar}
      >
        {/* Im Scroller, nicht darüber: Die Überschrift rollt mit der Liste weg — wie im
            Projekt und in Kurz notiert (#273). */}
        {!isEmpty && (
          <div className="done-page__header">
            <div className="page-help__row">
              <h1 ref={titleRef} className="done-page__title">
                <CheckCircleFilledIcon size={18} className="done-page__title-icon" />
                {t('done.title')}
              </h1>
              <PageHelpButton page="done" />
            </div>
            <p className="done-page__subtitle">{t('done.subtitle')}</p>
          </div>
        )}
        {isEmpty ? (
          <EmptyState
            illustration={<CalmIllustration />}
            title={t(`done.emptyTitles.${emptyTitleIndex}`)}
            text={t('done.emptyText')}
            titleAside={<PageHelpButton page="done" />}
          />
        ) : (
          <>
            {/*
              **Der Kasten steht immer** (Abnahme F-GE1, 21.09.2026). Hier stand
              eine Bedingung: Drei Nullen seien keine Zusammenfassung, also bleibe
              er weg. Beim Durchklicken las sich das andersherum — wer gestern oder
              letzte Woche gearbeitet hat, sah über seiner vollen Liste gar nichts,
              und die Serie auf null ist selbst eine Aussage. Ganz leer ist die Seite
              ohnehin der Zweig darüber, der den leeren Zustand zeigt.
            */}
            <DoneStats today={stats.today} week={stats.week} streak={stats.streak} />
            <ListFocusContext.Provider value={navigation.restoreListFocus}>
            <div
              ref={navigation.wrapperRef}
              className="done-page__days"
              tabIndex={-1}
              onKeyDown={navigation.onKeyDown}
              onPointerDownCapture={navigation.onPointerDownCapture}
            >
              {/* Eine Auswahl-Hülle über alle Tage: Die Markierung fährt auch von Tag zu Tag (#293). */}
              <SelectionHullScope>
              {/* Ein Budget für alle Listen der Seite (#303): Sonst baute jede ihre ersten Zeilen, auch weit unten. */}
              <LazyRowScope>
              {visibleDays.map((day: DoneDay<DoneTaskEntry>) => (
                <DoneDayGroup
                  key={day.key}
                  label={dayLabel(day.date, now)}
                  count={day.entries.length}
                  // Die Summe des Tages, aus denselben Zahlen wie die Zeilen —
                  // eine zweite Abfrage dafür wäre eine zweite Wahrheit.
                  focusedSeconds={day.entries.reduce((summe, e) => summe + (e.focusedSeconds ?? 0), 0)}
                >
                  <TaskList
                    tasks={day.entries.map(e => e.entry)}
                    stateKey={`done-${lifeSpace}-${day.key}`}
                    menuKind="done"
                    projectBadgePlacement="trailing"
                    hidePlay
                    externalSelection={navigation.getExternalSelection(day.entries.map(e => e.id))}
                    onNavigateEdit={navigation.navigateEdit}
                    onToggleComplete={handleToggle}
                    onDelete={handleDeleteTask}
                    onShowInProject={handleShowInProject}
                    onReopen={handleReopen}
                    onUpdateTask={handleUpdateTask}
                  />
                </DoneDayGroup>
              ))}
              </LazyRowScope>
              </SelectionHullScope>
            </div>
            </ListFocusContext.Provider>
            {hasMore && <div ref={sentinelRef} className="done-page__sentinel" aria-hidden="true" />}
          </>
        )}
      </div>
      </div>
    </div>
  )
}
